import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import { Grid } from "@material-ui/core";
import { useDispatch } from "react-redux";
import { FormContent, FormWrapper, MinistryAddNew, MinistryDelete, ButtonContainer, StaffCancelButton } from "./style";
import { SaveButton } from "../../Forms/Common/style";
import ToastContext from "../../../util/toastContext";

export const FORM_TYPES = {
  staff: "staff",
  ministry: "ministry",
};

// ID's for roles from the `{BASE_URL}/roles` endpoint
const ROLE_IDS = {
  PROCUREMENT_OFFICER: 1,
  PERMANENT_SECRETARY: 2,
  COMMISSIONER: 3,
  GOVERNOR: 4,
  KWPPA: 5,
  MODERATOR: 6,
  EXECUTIVE: 7,
  SSG: 8,
  MINISTRY_OF_JUSTICE: 9,
  ADMIN: 10,
  TENDER_BOARD: 11,
  CONTRACTORS_REG_BOARD: 12,
  MINISTERIAL_TENDER_BOARD: 13,
  ACCOUNTING_OFFICERS: 14,
  PARASTATALS_TENDERS_BOARD: 15,
};

const SingleTabForm = ({ handleCancel, children, defaultValues, multipleInviteAPI, formType, submitKey }) => {
  const [value, setValue] = useState(1);
  const [creatingStaff, setCreatingStaff] = useState(false);
  const [formValues, setFormValues] = useState([{}]);
  const [errors, setErrors] = useState([{}]);
  const [generalError, setGeneralError] = useState({});
  const [disableMinistryField, setDisableMinistryField] = useState(false);
  const dispatch = useDispatch();
  const { showAlert } = useContext(ToastContext);

  const handleChange = (e, i) => {
    const arr = formValues;
    const rolesWithoutMinistry = ["4", "5", "7", "8", "9", "10", "12"];
    arr[i][e.target.name] = e.target.value;
    console.log("arr", arr[i]);
    setFormValues(arr);
    delete generalError[i];
    setGeneralError(generalError);
    if (rolesWithoutMinistry.includes(arr[i]["roleId"])) {
      setDisableMinistryField(true);
    } else {
      setDisableMinistryField(false);
    }
  };

  const validateInput = (i, name, cb) => {
    const handleError = (message) => {
      const errorArray = [...errors];
      if (message) {
        !!errorArray[i] !== true && errorArray.push({});
        errorArray[i][name] = message;
        setErrors(errorArray);
      } else if (errorArray[i] && errorArray[i][name]) {
        delete errorArray[i][name];
        setErrors(errorArray);
      }
    };

    cb(formValues[i][name], handleError);
  };

  const isMinistryRequired = (values) => {
    const { roleId, ministryId } = values;
    const rolesWithMinistries = [
      ROLE_IDS.PROCUREMENT_OFFICER,
      ROLE_IDS.PERMANENT_SECRETARY,
      ROLE_IDS.COMMISSIONER,
      ROLE_IDS.MODERATOR,
      ROLE_IDS.MINISTERIAL_TENDER_BOARD,
    ];
    if (rolesWithMinistries.includes(parseInt(roleId))) {
      if (!!ministryId === true) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const areFormFieldsValid = () => {
    let isError = false;
    const currentArr = Object.values(formValues[value - 1]);

    switch (formType) {
      case FORM_TYPES.staff:
        for (let i = 0; i < formValues.length; i++) {
          if (!!formValues[i].roleId !== true || !!formValues[i].email !== true || !isMinistryRequired(formValues[i])) {
            isError = true;
            return setGeneralError({ [i]: "Please fill required fields" });
          }
        }
        break;
      default:
        if (
          (errors.length && Object.values(errors[errors.length - 1]).length) ||
          currentArr.filter(String).length !== Object.keys(defaultValues).length
        ) {
          isError = true;
          return setGeneralError({ [value - 1]: "All fields are required" });
        }
    }
    return !isError;
  };

  const renderError = ({ message, errors }) => {
    return (
      <div>
        <div style={{ fontWeight: "bold" }}>{message}</div>
        <div>
          {Object.keys(errors).map((errorPath) => (
            <div style={{ marginTop: 10, fontSize: "0.8em" }} key={errorPath}>
              <span style={{ textTransform: "capitalize" }}>{errorPath}</span> - {errors[errorPath]}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const api = multipleInviteAPI(submitKey === "userInvite" ? formValues : { [submitKey]: formValues });
    if (areFormFieldsValid()) {
      setCreatingStaff(true);
      dispatch(api).then((res) => {
        setCreatingStaff(false);
        if (res.error) {
          const errorPayload = JSON.parse(res.payload);
          showAlert({
            severity: "error",
            message: renderError({
              message: errorPayload.message,
              errors: errorPayload.errors,
            }),
          });
          return;
        }
        showAlert({ severity: "success", message: res.payload.message });
        handleCancel();
      });
    }
  };

  const childrenWithProps = (i) =>
    React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, {
          handleChange,
          formIndex: i,
          validateInput,
          errors,
          generalError,
          disableMinistryField,
        });
      }

      return child;
    });

  const updateFormArray = (val, action) => {
    const currentArr = Object.entries(formValues[val - 1]);
    const validationStatement =
      formType === FORM_TYPES.staff
        ? !!formValues[val - 1].roleId === true &&
          !!formValues[val - 1].email === true &&
          isMinistryRequired(formValues[val - 1]) === true
        : currentArr.filter(String).length === Object.keys(defaultValues).length;

    if (action === "inc") {
      if (validationStatement) {
        setValue(val + 1);
        const arr = formValues;
        arr[val] = { ...defaultValues };
        setFormValues(arr);
      } else {
        setGeneralError({ [val - 1]: formType === FORM_TYPES.staff ? "Please fill required fields" : "All fields are required" });
      }
    } else {
      setValue(val - 1);
      formValues.pop();
      setFormValues(formValues);
    }
  };

  return (
    <form>
      <FormWrapper>
        <FormContent>
          <Grid container item>
            {[...Array(value)].map((val, i) => childrenWithProps(i))}
          </Grid>
          {Boolean(value - 1) && (
            <Grid item>
              <MinistryDelete onClick={() => updateFormArray(value, "dec")}>Delete</MinistryDelete>
            </Grid>
          )}
        </FormContent>
        <Grid>
          <MinistryAddNew onClick={() => updateFormArray(value, "inc")}>+ Add New</MinistryAddNew>
        </Grid>
      </FormWrapper>
      <ButtonContainer>
        <StaffCancelButton onClick={handleCancel}>Cancel</StaffCancelButton>
        <SaveButton disabled={creatingStaff} onClick={(e) => onSubmit(e)}>
          {creatingStaff ? "Please wait..." : "Create"}
        </SaveButton>
      </ButtonContainer>
    </form>
  );
};

SingleTabForm.prototypes = {
  handleCancel: PropTypes.func,
  children: PropTypes.node,
  defaultValues: PropTypes.Object,
  multipleInviteAPI: PropTypes.func,
  formType: PropTypes.oneOf(FORM_TYPES),
  submitKey: PropTypes.string,
};

export default SingleTabForm;
